import { useCallback, useEffect, useState } from 'react';

import type { FormState } from 'final-form';
import { useForm } from 'react-final-form';

import { useTracking } from '@change-corgi/core/react/tracking';

import { useQueryParamValue } from 'src/app/shared/hooks/location';

import { MAX_TITLE_LENGTH, SUGGESTED_TEMPLATE_NOTICE, TITLE_INPUT_FOCUS_EVENT } from '../../../../../../constants';
import { useCreatePetitionUpdateContext } from '../../../../context';
import type { PetitionUpdateFormValues, TitleInputProps, UseTitleInputResult } from '../../../../types';

export function useTitleInput({ initialValue }: TitleInputProps): UseTitleInputResult {
	const [totalCharactersRemaining, setTotalCharactersRemaining] = useState(
		MAX_TITLE_LENGTH - (initialValue?.length || 0),
	);
	const form = useForm();
	const track = useTracking();
	const sourceLocation = useQueryParamValue('source_location');
	const isTemplateFilled = useCreatePetitionUpdateContext();

	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		form.mutators.setTitle?.(initialValue);
	}, [initialValue, form.mutators]);

	const handleFormChange = useCallback(
		({ values }: FormState<PetitionUpdateFormValues, PetitionUpdateFormValues>) => {
			const titleLength = values.title?.length || 0;
			setTotalCharactersRemaining(MAX_TITLE_LENGTH - titleLength);
		},
		[setTotalCharactersRemaining],
	);

	const trackFocus = useCallback(() => {
		void track(TITLE_INPUT_FOCUS_EVENT, {
			petition_update_template: isTemplateFilled ? SUGGESTED_TEMPLATE_NOTICE : undefined,
			source_location: sourceLocation,
			edit: false,
		});
	}, [track, isTemplateFilled, sourceLocation]);

	return {
		data: {
			totalCharactersRemaining,
		},
		actions: {
			handleFormChange,
			trackFocus,
		},
	};
}
